/* Plan — the founder's brain. Three full-width, stacked regions:
   1. Founder inbox — a prominent composer POSTs to /api/inbox; every idea is
      editable (pencil → inline Textarea), attributed (author + timestamp), and
      hand-triageable (an expandable "triage" row: bucket / tags / profit ×
      effort × CoI-safety scores). Auto-triage by the crew stays the default;
      the founder can override any of it from the UI.
   2. Project queue — Notion Company Roadmap → Now / Next / Later, with an
      always-visible legend explaining how the lanes + card fields map.
   3. My [I DO] list — checkable founder-only gates (D1-backed), full-width so
      the add-input is never truncated; crew-flagged items carry an "auto" tag.
   Every data region ships all four states; POSTs toast on failure, audit on
   success; optimistic patches keep edit/triage/toggle demonstrable on preview
   (where the API 202s because Notion/D1 aren't wired yet). Esc closes any
   inline editor; color is never the only signal (buckets/lanes carry a word). */
import { useState, type CSSProperties, type ReactNode } from 'react';
import { Badge, Button, Checkbox, IconButton, Input, Select, Tag, Textarea } from '../../ds';
import { WidgetBody } from '../../ds/mc-kit';
import { useContract } from '../../lib/api';
import { integrationFor } from '../../lib/flags';
import type { IdoItem, InboxBucket, InboxIdea, InboxScore, PlanItem } from '../../lib/contract';
import { useOps } from '../ops';
import { Kicker, Panel } from '../ui';

const mono: CSSProperties = { fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--muted)' };
const microLabel: CSSProperties = { fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--muted)' };
const smallTag: CSSProperties = { padding: '2px 9px', fontSize: 10 };
const wrap: CSSProperties = { overflowWrap: 'anywhere', wordBreak: 'break-word' };

const BUCKET_TONE: Record<InboxBucket, 'neutral' | 'iris' | 'cyan'> = {
  idea: 'neutral',
  backlog: 'iris',
  project: 'cyan',
};
const BUCKET_WORD: Record<InboxBucket, string> = { idea: 'Idea', backlog: 'Backlog', project: 'Project' };
const BUCKETS: InboxBucket[] = ['idea', 'backlog', 'project'];

const PLAN_COLS: { key: 'now' | 'next' | 'later'; name: string; accent: string; note: string }[] = [
  { key: 'now', name: 'Now', accent: 'var(--accent)', note: 'in active build, shipping this cycle' },
  { key: 'next', name: 'Next', accent: 'var(--primary)', note: 'teed up right after Now' },
  { key: 'later', name: 'Later', accent: 'var(--muted)', note: 'parked or still researching' },
];

/** Composite the server would compute — mirrored so the total updates live. */
const computeTotal = (profit: number, effort: number, safety: number) =>
  Math.round(((profit + safety + (6 - effort)) / 15) * 100);

async function postJson(url: string, body: unknown): Promise<Response> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`${url} → ${res.status}`);
  return res;
}

/* ---------- local line icons (SVG, not emoji) ---------- */
function Ico({ d, size = 15, sw = 2 }: { d: string | string[]; size?: number; sw?: number }) {
  const paths = Array.isArray(d) ? d : [d];
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths.map((p, i) => (
        <path key={i} d={p} />
      ))}
    </svg>
  );
}
const PENCIL = ['M12 20h9', 'M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z'];
const USER = ['M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8', 'M4 21a8 8 0 0 1 16 0'];
const X_MARK = 'M18 6 6 18M6 6l12 12';

function ScoreChip({ score }: { score: InboxScore }) {
  const sub = `profit ${score.profit} · effort ${score.effort} · safety ${score.safety}`;
  return (
    <span title={sub} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, minWidth: 0, flexWrap: 'wrap' }}>
      <Badge tone="cyan" mono>score {score.total}</Badge>
      <span style={mono}>{sub}</span>
    </span>
  );
}

/* ---------- a compact 1–5 stepper (labelled Select) ---------- */
function ScoreSelect({ label, value, onChange }: { label: string; value: number; onChange: (n: number) => void }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: '1 1 84px', minWidth: 0 }}>
      <span style={microLabel}>{label}</span>
      <Select
        aria-label={`${label} (1–5)`}
        value={String(value)}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onChange(parseInt(e.target.value, 10))}
        style={{ padding: '8px 30px 8px 10px', fontSize: 13 }}
      >
        {[1, 2, 3, 4, 5].map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </Select>
    </label>
  );
}

interface IdeaRowProps {
  idea: InboxIdea;
  last: boolean;
  onSaveEdit: (text: string) => Promise<boolean>;
  onBucket: (bucket: InboxBucket) => void;
  onSetTags: (tags: string[]) => void;
  onSetScore: (profit: number, effort: number, safety: number) => void;
}

function IdeaRow({ idea, last, onSaveEdit, onBucket, onSetTags, onSetScore }: IdeaRowProps) {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(idea.text);
  const [triageOpen, setTriageOpen] = useState(false);
  const [tagDraft, setTagDraft] = useState('');

  const p = idea.score?.profit ?? 3;
  const ef = idea.score?.effort ?? 3;
  const sa = idea.score?.safety ?? 3;

  const startEdit = () => {
    setEditText(idea.text);
    setEditing(true);
  };
  const cancelEdit = () => {
    setEditing(false);
    setEditText(idea.text);
  };
  const saveEdit = async () => {
    const t = editText.trim();
    if (!t) return;
    if (t === idea.text) {
      setEditing(false);
      return;
    }
    if (await onSaveEdit(t)) setEditing(false);
  };
  const addTag = () => {
    const t = tagDraft.trim().slice(0, 24);
    if (!t || idea.tags.includes(t) || idea.tags.length >= 6) {
      setTagDraft('');
      return;
    }
    onSetTags([...idea.tags, t]);
    setTagDraft('');
  };

  return (
    <div style={{ padding: '12px 2px', borderBottom: last ? 'none' : '1px solid var(--border)' }}>
      {/* text + edit affordance */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        {editing ? (
          <div
            style={{ flex: 1, minWidth: 0 }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.stopPropagation();
                cancelEdit();
              }
            }}
          >
            <Textarea
              aria-label="Edit idea"
              autoFocus
              rows={3}
              value={editText}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditText(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault();
                  void saveEdit();
                }
              }}
              hint="Cmd/Ctrl + Enter to save · Esc to cancel"
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
              <Button variant="ghost" size="sm" onClick={cancelEdit}>
                Cancel
              </Button>
              <Button size="sm" onClick={() => void saveEdit()} disabled={!editText.trim()}>
                Save
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: 'var(--ink)', lineHeight: 1.5, ...wrap }}>{idea.text}</div>
            <IconButton size="sm" variant="ghost" label="Edit idea" onClick={startEdit}>
              <Ico d={PENCIL} size={15} />
            </IconButton>
          </>
        )}
      </div>

      {/* meta: bucket + score + tags + attribution + triage toggle */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px 8px', marginTop: 8 }}>
        <Tag tone={BUCKET_TONE[idea.bucket]} style={smallTag}>
          {BUCKET_WORD[idea.bucket]}
        </Tag>
        {idea.score ? <ScoreChip score={idea.score} /> : <Badge tone="warn">awaiting triage</Badge>}
        {idea.tags.map((t) => (
          <Tag key={t} style={smallTag}>
            {t}
          </Tag>
        ))}
        <span
          aria-label={`posted by ${idea.author}, ${idea.time}`}
          title={`Posted by ${idea.author}`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginLeft: 'auto', minWidth: 0, ...mono }}
        >
          <Ico d={USER} size={12} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 120 }}>{idea.author}</span>
          <span aria-hidden="true">·</span>
          <span>{idea.time}</span>
        </span>
        <button
          type="button"
          aria-expanded={triageOpen}
          onClick={() => setTriageOpen((o) => !o)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 10px',
            minHeight: 30,
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border)',
            background: triageOpen ? 'var(--surface2)' : 'transparent',
            color: 'var(--muted)',
            fontFamily: 'var(--font-mono)',
            fontSize: 10.5,
            cursor: 'pointer',
          }}
        >
          <span aria-hidden="true" style={{ display: 'inline-flex', transform: triageOpen ? 'rotate(180deg)' : 'none', transition: 'transform var(--dur) ease' }}>
            <Ico d="M6 9l6 6 6-6" size={12} />
          </span>
          {triageOpen ? 'close' : 'triage'}
        </button>
      </div>

      {/* expandable manual-triage editor */}
      {triageOpen && (
        <div
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              setTriageOpen(false);
            }
          }}
          style={{ marginTop: 10, padding: 12, borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--bg)', display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          <span style={{ ...microLabel, letterSpacing: '0.08em' }}>manual triage · overrides the crew</span>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: '1 1 130px', minWidth: 0 }}>
              <span style={microLabel}>Bucket</span>
              <Select
                aria-label="Bucket"
                value={idea.bucket}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onBucket(e.target.value as InboxBucket)}
                style={{ padding: '8px 30px 8px 10px', fontSize: 13 }}
              >
                {BUCKETS.map((b) => (
                  <option key={b} value={b}>
                    {BUCKET_WORD[b]}
                  </option>
                ))}
              </Select>
            </label>
            <ScoreSelect label="Profit" value={p} onChange={(n) => onSetScore(n, ef, sa)} />
            <ScoreSelect label="Effort" value={ef} onChange={(n) => onSetScore(p, n, sa)} />
            <ScoreSelect label="Safety" value={sa} onChange={(n) => onSetScore(p, ef, n)} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={microLabel}>Total</span>
              <Badge tone="cyan" mono>
                {idea.score ? idea.score.total : computeTotal(p, ef, sa)}
              </Badge>
            </div>
          </div>

          <div>
            <span style={{ ...microLabel, display: 'block', marginBottom: 6 }}>Tags</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              {idea.tags.length === 0 && <span style={mono}>no tags yet</span>}
              {idea.tags.map((t) => (
                <span
                  key={t}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 4px 2px 10px', fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--muted)', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', maxWidth: '100%' }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t}</span>
                  <button
                    type="button"
                    aria-label={`Remove tag ${t}`}
                    onClick={() => onSetTags(idea.tags.filter((x) => x !== t))}
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, flex: 'none', padding: 0, borderRadius: '50%', border: 'none', background: 'transparent', color: 'var(--muted)', cursor: 'pointer' }}
                  >
                    <Ico d={X_MARK} size={11} sw={2.4} />
                  </button>
                </span>
              ))}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
              <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                <Input
                  aria-label="Add a tag"
                  placeholder="Add a tag"
                  value={tagDraft}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTagDraft(e.target.value)}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTag();
                    } else if (e.key === 'Escape') {
                      e.stopPropagation();
                      setTriageOpen(false);
                    }
                  }}
                />
              </div>
              <Button variant="secondary" size="sm" onClick={addTag} disabled={!tagDraft.trim() || idea.tags.length >= 6}>
                Add tag
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PlanColumn({ name, accent, note, items }: { name: string; accent: string; note: string; items: PlanItem[] }) {
  return (
    <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 12, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: 'var(--font-mono)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--muted)' }}>
          <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: accent, flex: 'none' }} />
          {name}
        </span>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' }}>{items.length}</span>
      </div>
      <div style={{ ...mono, marginBottom: 10, ...wrap }}>{note}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.length === 0 && <span style={{ ...mono, padding: '4px 0' }}>nothing queued</span>}
        {items.map((it, i) => (
          <div key={i} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 10 }}>
            <div style={{ fontSize: 12.5, color: 'var(--ink)', lineHeight: 1.45, ...wrap }}>{it.t}</div>
            <div style={{ ...mono, marginTop: 5, ...wrap }}>
              {it.owner || 'unassigned'} · {it.status.toLowerCase()} · {it.area}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function QueueLegend() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, color: 'var(--muted)' }}>
        Pulled from the Notion <em>Company Roadmap</em>. Each item’s Priority sets its lane — <em>P0 → Now</em>, <em>P1 → Next</em>, <em>P2 / Parked → Later</em>.
      </p>
      <details style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '8px 12px', background: 'var(--bg)' }}>
        <summary style={{ cursor: 'pointer', ...microLabel, letterSpacing: '0.08em' }}>how the queue works</summary>
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.55 }}>
          {PLAN_COLS.map((c) => (
            <span key={c.key} style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
              <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: c.accent, flex: 'none', transform: 'translateY(1px)' }} />
              <span>
                <em>{c.name}</em> — {c.note}.
              </span>
            </span>
          ))}
          <span style={{ ...mono, ...wrap }}>each card shows owner · status · area — who holds it, its Roadmap state, and the part of the business it touches.</span>
        </div>
      </details>
    </div>
  );
}

function ConnectNote({ children }: { children: ReactNode }) {
  return <div style={{ marginTop: 10, fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--muted)' }}>{children} · showing sample data</div>;
}

export function Plan() {
  const { pushToast, appendAudit } = useOps();
  const inbox = useContract('inbox', { pollMs: 30_000 });
  const plan = useContract('plan', { pollMs: 60_000 });
  const ido = useContract('ido', { pollMs: 30_000 });

  const inboxConnect = integrationFor('inbox')?.connect;
  const planConnect = integrationFor('plan')?.connect;
  const idoConnect = integrationFor('ido')?.connect;

  /* ---- brain-dump composer ---- */
  const [draft, setDraft] = useState('');
  const [capturing, setCapturing] = useState(false);
  // Ideas accepted by the stub endpoint (202 — no store yet) still show up.
  const [localIdeas, setLocalIdeas] = useState<InboxIdea[]>([]);
  // Optimistic edit/triage overrides, kept so a 202 (not persisted) still sticks
  // even across a refetch that returns the unchanged sample slice.
  const [patches, setPatches] = useState<Record<string, Partial<InboxIdea>>>({});

  const capture = async () => {
    const text = draft.trim();
    if (!text || capturing) return;
    setCapturing(true);
    try {
      const res = await postJson('/api/inbox', { text });
      pushToast('success', 'Captured — Atlas will triage it');
      appendAudit({ actor: 'Cassidy', action: 'Captured idea', target: text.slice(0, 60) });
      setDraft('');
      if (res.status === 202) {
        setLocalIdeas((l) => [{ id: `local-${Date.now()}`, text, bucket: 'idea', tags: [], score: null, time: 'now', author: 'Cassidy' }, ...l]);
      }
      inbox.refetch();
    } catch {
      pushToast('danger', 'Capture failed', 'The inbox didn’t take it — your text is still in the box.');
    } finally {
      setCapturing(false);
    }
  };

  /** Optimistic edit/triage: patch first, POST (skip for local-only ideas),
      toast + audit on success, revert the patch on failure. Returns success. */
  const mutateIdea = async (
    id: string,
    patch: Partial<InboxIdea>,
    body: Record<string, unknown>,
    auditAction: string,
    target: string,
    okTitle: string,
  ): Promise<boolean> => {
    const prev = patches[id];
    setPatches((pp) => ({ ...pp, [id]: { ...pp[id], ...patch } }));
    try {
      if (!id.startsWith('local-')) await postJson('/api/inbox', body);
      appendAudit({ actor: 'Cassidy', action: auditAction, target: target.slice(0, 60) });
      pushToast('success', okTitle);
      inbox.refetch();
      return true;
    } catch {
      setPatches((pp) => {
        const next = { ...pp };
        if (prev) next[id] = prev;
        else delete next[id];
        return next;
      });
      pushToast('danger', 'That change didn’t save', 'The inbox rejected it — reverted.');
      return false;
    }
  };

  const rawIdeas = [...localIdeas, ...(inbox.data ?? [])];
  const ideas = rawIdeas.map((idea) => (patches[idea.id] ? { ...idea, ...patches[idea.id] } : idea));
  const inboxState = inbox.state === 'empty' && ideas.length ? 'live' : inbox.state;
  const showInboxConnect = inbox.isPlaceholder && !!inboxConnect;

  /* ---- project queue ---- */
  const showPlanConnect = plan.isPlaceholder && !!planConnect;
  const queued = plan.data ? plan.data.now.length + plan.data.next.length + plan.data.later.length : 0;

  /* ---- [I DO] list ---- */
  const [newIdo, setNewIdo] = useState('');
  const [addingIdo, setAddingIdo] = useState(false);
  const [localIdos, setLocalIdos] = useState<IdoItem[]>([]);
  // Optimistic toggle overrides, kept so a 202 (not persisted) still sticks.
  const [flips, setFlips] = useState<Record<string, boolean>>({});

  const idoItems = [...localIdos, ...(ido.data ?? [])].map((it) => (flips[it.id] !== undefined ? { ...it, done: flips[it.id] } : it));
  const openIdos = idoItems.filter((it) => !it.done);
  const doneIdos = idoItems.filter((it) => it.done);
  const sortedIdos = [...openIdos, ...doneIdos];
  const idoState = ido.state === 'empty' && idoItems.length ? 'live' : ido.state;
  const showIdoConnect = ido.isPlaceholder && !!idoConnect;

  const toggleIdo = async (it: IdoItem) => {
    const next = !it.done;
    setFlips((f) => ({ ...f, [it.id]: next })); // optimistic
    try {
      // Locally-added items (stub mode) have no server row to update.
      if (!it.id.startsWith('local-')) await postJson('/api/ido', { op: 'toggle', id: it.id, done: next });
      appendAudit({ actor: 'Cassidy', action: next ? 'Checked off' : 'Reopened', target: it.text.slice(0, 60) });
      ido.refetch();
    } catch {
      setFlips((f) => ({ ...f, [it.id]: it.done })); // revert
      pushToast('danger', 'Couldn’t update the list', 'The toggle didn’t save — try again.');
    }
  };

  const addIdo = async () => {
    const text = newIdo.trim();
    if (!text || addingIdo) return;
    setAddingIdo(true);
    try {
      const res = await postJson('/api/ido', { op: 'add', text });
      appendAudit({ actor: 'Cassidy', action: 'Added [I DO]', target: text.slice(0, 60) });
      pushToast('success', 'On the list', text.slice(0, 60));
      setNewIdo('');
      if (res.status === 202) {
        setLocalIdos((l) => [{ id: `local-${Date.now()}`, text, done: false, source: 'me', time: 'now' }, ...l]);
      }
      ido.refetch();
    } catch {
      pushToast('danger', 'Couldn’t add it', 'The list didn’t save your item — try again.');
    } finally {
      setAddingIdo(false);
    }
  };

  return (
    <div data-screen-label="Plan" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--mc-gap)' }}>

      {/* === 1 · FOUNDER INBOX (full width) === */}
      <Panel
        title="// founder inbox"
        label="Founder inbox"
        accent
        aside={showInboxConnect ? <Badge tone="warn">connect me</Badge> : undefined}
      >
        <Textarea
          aria-label="Drop an idea"
          placeholder="Drop an idea — a feature, a fix, a hunch. The crew takes it from here."
          rows={3}
          value={draft}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDraft(e.target.value)}
          onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
              e.preventDefault();
              void capture();
            }
          }}
          hint="Cmd/Ctrl + Enter to capture"
        />
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginTop: 10 }}>
          <Kicker style={{ flex: '1 1 240px', minWidth: 0, lineHeight: 1.6 }}>
            // crew auto-triages every idea — you can edit, re-bucket, tag & score any of them by hand
          </Kicker>
          <Button onClick={() => void capture()} disabled={!draft.trim() || capturing}>
            {capturing ? 'Capturing…' : 'Capture'}
          </Button>
        </div>

        <div style={{ borderTop: '1px solid var(--border)', marginTop: 14, paddingTop: 4 }}>
          <WidgetBody
            state={inboxState}
            rows={3}
            minHeight={140}
            emptyLabel="No ideas yet — drop the first one above."
            emptySub="Everything you capture lands here for triage."
            errorLabel="Inbox unreachable"
            errorSub="Couldn’t reach Notion or the local queue."
            onRetry={inbox.refetch}
          >
            <div>
              {ideas.map((idea, i) => (
                <IdeaRow
                  key={idea.id}
                  idea={idea}
                  last={i === ideas.length - 1}
                  onSaveEdit={(text) => mutateIdea(idea.id, { text }, { op: 'edit', id: idea.id, text }, 'Edited idea', text, 'Idea updated')}
                  onBucket={(bucket) => void mutateIdea(idea.id, { bucket }, { op: 'triage', id: idea.id, bucket }, 'Re-bucketed idea', `${idea.text} → ${BUCKET_WORD[bucket]}`, `Moved to ${BUCKET_WORD[bucket]}`)}
                  onSetTags={(tags) => void mutateIdea(idea.id, { tags }, { op: 'triage', id: idea.id, tags }, 'Retagged idea', idea.text, 'Tags updated')}
                  onSetScore={(profit, effort, safety) =>
                    void mutateIdea(
                      idea.id,
                      { score: { profit, effort, safety, total: computeTotal(profit, effort, safety) } },
                      { op: 'triage', id: idea.id, score: { profit, effort, safety } },
                      'Scored idea',
                      idea.text,
                      'Score updated',
                    )
                  }
                />
              ))}
            </div>
          </WidgetBody>
        </div>
        {showInboxConnect && <ConnectNote>{inboxConnect}</ConnectNote>}
      </Panel>

      {/* === 2 · PROJECT QUEUE (full width, widened) === */}
      <Panel
        title="// project queue"
        label="Project queue"
        aside={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {showPlanConnect && <Badge tone="warn">connect me</Badge>}
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent-ink)' }}>{queued} queued</span>
          </span>
        }
      >
        <QueueLegend />
        <div style={{ marginTop: 14 }}>
          <WidgetBody
            state={plan.state}
            rows={3}
            minHeight={200}
            emptyLabel="Queue is clear"
            errorLabel="Roadmap unreachable"
            errorSub="Couldn’t reach the Notion Roadmap."
            onRetry={plan.refetch}
          >
            {plan.data &&
              (queued === 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 140, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
                  Queue is clear — promote an idea from the inbox above.
                </div>
              ) : (
                <div className="mc-auto mc-auto-sm">
                  {PLAN_COLS.map((col) => (
                    <PlanColumn key={col.key} name={col.name} accent={col.accent} note={col.note} items={plan.data![col.key]} />
                  ))}
                </div>
              ))}
          </WidgetBody>
        </div>
        {showPlanConnect && <ConnectNote>{planConnect}</ConnectNote>}
      </Panel>

      {/* === 3 · MY [I DO] LIST (full width) === */}
      <Panel
        title="// my [i do] list"
        label="My [I DO] list"
        aside={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {showIdoConnect && <Badge tone="warn">connect me</Badge>}
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent-ink)' }}>{openIdos.length} open</span>
          </span>
        }
      >
        <WidgetBody
          state={idoState}
          rows={3}
          minHeight={160}
          emptyLabel="All clear — nothing needs your signature."
          emptySub="Founder-only gates the crew flags land here."
          errorLabel="List unavailable"
          errorSub="Couldn’t reach the D1 list."
          onRetry={ido.refetch}
        >
          <div className="mc-scroll" style={{ maxHeight: 380, overflowY: 'auto' }}>
            {sortedIdos.map((it, i) => (
              <div
                key={it.id}
                style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, borderBottom: i === sortedIdos.length - 1 ? 'none' : '1px solid var(--border)', padding: '2px 0' }}
              >
                <Checkbox
                  checked={it.done}
                  onChange={() => void toggleIdo(it)}
                  style={{ flex: 1, minWidth: 0, minHeight: 44 }}
                  label={
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 8,
                        fontSize: 13.5,
                        lineHeight: 1.45,
                        color: it.done ? 'var(--muted)' : 'var(--ink)',
                        textDecoration: it.done ? 'line-through' : 'none',
                        ...wrap,
                      }}
                    >
                      {it.text}
                      {it.source === 'auto' && <Tag style={{ padding: '1px 7px', fontSize: 9.5 }}>auto</Tag>}
                    </span>
                  }
                />
                <span style={{ ...mono, flex: 'none' }}>{it.time}</span>
              </div>
            ))}
          </div>
        </WidgetBody>

        {/* add row — full-width Input on its own line so it never truncates */}
        <div style={{ marginTop: 14 }}>
          <Input
            aria-label="Add an [I DO]"
            placeholder="Add an [I DO] — something only you can do"
            value={newIdo}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewIdo(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void addIdo();
              }
            }}
          />
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginTop: 10 }}>
            <Kicker style={{ flex: '1 1 200px', minWidth: 0, lineHeight: 1.6 }}>
              // founder-only gates — the crew flags these; add your own here
            </Kicker>
            <Button onClick={() => void addIdo()} disabled={!newIdo.trim() || addingIdo}>
              {addingIdo ? 'Adding…' : 'Add'}
            </Button>
          </div>
        </div>
        {showIdoConnect && <ConnectNote>{idoConnect}</ConnectNote>}
      </Panel>
    </div>
  );
}
